import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, Phone, Upload, X, FileText, Download, Trash2, Paperclip, Eye, Sparkles, IndianRupee, Receipt, Send, Check, Mail } from 'lucide-react';
import { hasPermission } from '../utils/permissions';
import { useAuth } from '../contexts/AuthContext';
import AISalesAssistant from './AISalesAssistant';
import ProposalModal from './ProposalModal';
import AgreementModal from './AgreementModal';
import InvoiceModal from './InvoiceModal';
import PaymentTrackingSection from './PaymentTrackingSection';
import ConfirmationModal from './ConfirmationModal';
import PromptModal from './PromptModal';
import CustomAlert from './CustomAlert';
import { useAlert } from '../hooks/useAlert';
import CountryDropdown from './CountryDropdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Native file download helper to bypass rrweb-recorder
const downloadFile = (url, headers = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.responseType = 'blob';
    
    // Set headers
    Object.keys(headers).forEach(key => {
      xhr.setRequestHeader(key, headers[key]);
    });
    
    xhr.onload = function() {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ ok: true, blob: xhr.response, status: xhr.status });
      } else {
        // For error responses, don't try to access responseText when responseType is blob
        reject({ ok: false, status: xhr.status, statusText: xhr.statusText, response: xhr.response });
      }
    };
    
    xhr.onerror = function() {
      reject({ ok: false, error: 'Network error', status: 0 });
    };
    
    xhr.send();
  });
};

const UPDATE_TYPES = [
  'Called',
  'Email Sent',
  'SMS Sent',
  'WhatsApp',
  'Meeting Scheduled',
  'Meeting Completed',
  'Document Requested',
  'Document Received',
  'Follow-up Required',
  'Sales Intimation',
  'Dialer-Missed - AgentNoAnswer',
  'Reject Lead',
  'Other'
];

const REJECTION_REASONS = [
  'Budget Constraints',
  'Not Interested in Service',
  'Already Availed Service from Another Provider',
  'Incomplete Documentation / Not Eligible',
  'Unresponsive / Not Reachable',
  'Open Work Visa',
  'Other'
];

const MARITAL_STATUS_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed'];
const SALES_LOCATIONS = ['Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Hyderabad', 'Pune', 'Kolkata', 'Other'];

export default function UpdateLeadModal({ lead, onClose, onUpdate }) {
  const { getAuthHeader, admin } = useAuth();
  
  const [formData, setFormData] = useState({
    update_type: '',
    rejection_reason: '',
    rejection_country: '',
    next_update_date: '',
    next_update_time: '',
    update_remark: '',
    dob: lead?.dob || '',
    marital_status: lead?.marital_status || '',
    sales_location: lead?.sales_location || '',
    process_owner: lead?.assigned_to || '',
    temperature: lead?.temperature || 'warm'
  });

  const [resume, setResume] = useState(null);
  const [otherDocs, setOtherDocs] = useState([]);
  const [updateHistory, setUpdateHistory] = useState({
    current_updates: [],
    past_updates: [],
    last_reassignment: null,
    current_count: 0,
    past_count: 0
  });
  const [processOwners, setProcessOwners] = useState([]);
  const [leadStages, setLeadStages] = useState([]);
  const [userRolePermissions, setUserRolePermissions] = useState({
    accessible_stages: [],
    viewable_stages: []
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'past'
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignData, setReassignData] = useState({
    assigned_to: '',
    reason: ''
  });
  const [userRole, setUserRole] = useState('');
  
  // Document management states
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [customDocName, setCustomDocName] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null); // For document preview modal
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // AI Follow-up states
  const [aiFollowupLoading, setAiFollowupLoading] = useState(false);
  const [aiFollowupResult, setAiFollowupResult] = useState(null);
  const [showAiFollowup, setShowAiFollowup] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Custom alert hook
  const { showAlert, showConfirm, showSuccess, showError, alertState, setAlertState } = useAlert();
  
  // Proposal states
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposals, setProposals] = useState([]);
  
  // Invoice states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  
  // Agreement states
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreements, setAgreements] = useState([]);

  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Notification and Confirmation Modal States
  const [notification, setNotification] = useState({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [confirmation, setConfirmation] = useState({
    show: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });
  const [promptModal, setPromptModal] = useState({
    show: false,
    title: '',
    message: '',
    placeholder: '',
    onConfirm: null
  });
  const [screenshotModal, setScreenshotModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null
  });
  const [screenshot, setScreenshot] = useState(null);
  const [currentApprovalTarget, setCurrentApprovalTarget] = useState(null); // {type: 'proposal'|'agreement', id: 'xxx'}
  const [approvingWithScreenshot, setApprovingWithScreenshot] = useState(false);

  // Template Email states
  const [showTemplateEmailModal, setShowTemplateEmailModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sendingTemplateEmail, setSendingTemplateEmail] = useState(false);

  const showNotification = (type, title, message) => {
    setNotification({ show: true, type, title, message });
  };

  const closeNotification = () => {
    setNotification({ ...notification, show: false });
  };

  // Update form data when lead prop changes
  useEffect(() => {
    if (lead) {
      setFormData(prev => ({
        ...prev,
        dob: lead.dob || '',
        marital_status: lead.marital_status || '',
        sales_location: lead.sales_location || '',
        process_owner: lead.assigned_to || '',
        temperature: lead.temperature || 'warm'
      }));
    }
  }, [lead.id, lead.dob, lead.marital_status, lead.sales_location, lead.assigned_to, lead.temperature]);

  useEffect(() => {
    fetchUpdateHistory();
    fetchProcessOwners();
    fetchLeadStages();
    fetchUserRolePermissions();
    fetchLeadDocuments();
    fetchProposals();
    fetchInvoices();
    fetchAgreements();
    // Get user role from localStorage
    const adminData = localStorage.getItem('admin_data');
    if (adminData) {
      const parsedData = JSON.parse(adminData);
      setUserRole(parsedData.role || '');
    }
  }, [lead]);
  
  // Check if lead is in document collection stage
  const isDocumentCollection = lead?.stage === 'document_collection';
  const isSuperAdmin = userRole === 'super_admin';
  
  const fetchProposals = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/proposals?lead_id=${lead.id}`,
        { headers: getAuthHeader() }
      );
      setProposals(response.data || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    }
  };
  
  const fetchInvoices = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/invoices?lead_id=${lead.id}`,
        { headers: getAuthHeader() }
      );
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  
  const fetchAgreements = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/agreements?lead_id=${lead.id}`,
        { headers: getAuthHeader() }
      );
      setAgreements(response.data || []);
    } catch (error) {
      console.error('Error fetching agreements:', error);
    }
  };

  

  
  const handleAgreementSuccess = () => {
    fetchAgreements();
    onUpdate(); // Refresh lead data
  };
  
  // Check if lead stage allows agreement creation (after proposal approval)
  const canCreateAgreement = () => {
    const approvedProposal = proposals.find(p => p.status === 'approved');
    return approvedProposal && ['proposal_sent', 'proposal_approved', 'agreement_sent', 'agreement_signed'].includes(lead.stage);
  };
  
  // Check if we should show generate invoice button (after agreement approval)
  const canCreateInvoice = () => {
    const approvedAgreement = agreements.find(a => a.status === 'approved');
    return approvedAgreement && lead.stage === 'agreement_signed';
  };

  
  // Generic download handler that refreshes history after download
  const handleDownloadWithHistory = async (downloadUrl, filename) => {
    try {
      const response = await downloadFile(
        downloadUrl,
        {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      );

      if (response.ok) {
        const url = window.URL.createObjectURL(response.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Refresh update history to show download event
        await fetchUpdateHistory();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  };

  // Download Agreement PDF
  const downloadAgreementPDF = async (agreementId, agreementNumber) => {
    try {
      await handleDownloadWithHistory(
        `${BACKEND_URL}/api/agreements/${agreementId}/download-pdf`,
        `agreement_${agreementNumber.replace(/\//g, '_')}.pdf`
      );
    } catch (error) {
      console.error('Error downloading agreement PDF:', error);
      showError('Failed to download agreement PDF', 'Download Error');
    }
  };
  
  // Approve Agreement
  const approveAgreement = async (agreementId) => {
    // For non-admin users, show screenshot upload modal
    if (admin?.role !== 'admin' && admin?.role !== 'super_admin') {
      setCurrentApprovalTarget({ type: 'agreement', id: agreementId });
      setScreenshot(null);
      setScreenshotModal({
        show: true,
        title: 'Upload Customer Acknowledgement',
        message: 'To approve this agreement, please upload a screenshot of the email received from the customer showing their acceptance or acknowledgement of the agreement. Our AI will verify the customer email automatically.',
        onConfirm: handleApprovalWithScreenshot
      });
      return;
    }
    
    // For super_admin/admin, show confirmation and approve directly
    setConfirmation({
      show: true,
      type: 'success',
      title: 'Approve Agreement?',
      message: 'This will mark the agreement as approved and update the lead stage to "Agreement Signed". The client will be able to proceed with payment.',
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/agreements/${agreementId}/approve`,
            { 
              approved: true,
              approval_method: 'super_admin_override'
            },
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchAgreements();
            onUpdate();
            showNotification('success', 'Agreement Approved', 'Agreement approved successfully! Lead stage updated to "Agreement Signed".');
          }
        } catch (error) {
          console.error('Error approving agreement:', error);
          showNotification('error', 'Approval Failed', error.response?.data?.detail || 'Failed to approve agreement');
        }
      }
    });
  };

  
  // Send Agreement
  const sendInvoice = async (invoiceId, invoiceNumber) => {
    setConfirmation({
      show: true,
      type: 'info',
      title: 'Send Invoice via Email?',
      message: `This will send invoice ${invoiceNumber} as a PDF attachment to the client's email address (${lead?.email || 'email on file'}).`,
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/invoices/${invoiceId}/send`,
            {},
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchInvoices();
            onUpdate();
            showNotification('success', 'Invoice Sent!', response.data?.message || `Invoice emailed successfully to ${lead?.email}.`);
          }
        } catch (error) {
          console.error('Error sending invoice:', error);
          showNotification('error', 'Email Failed', error.response?.data?.detail || 'Failed to send invoice email. Please check that the invoice PDF has been generated and the lead has a valid email address.');
        }
      }
    });
  };

  const sendAgreement = async (agreementId) => {
    setConfirmation({
      show: true,
      type: 'info',
      title: 'Send Agreement via Email?',
      message: `This will send the agreement as a PDF attachment to the client's email address (${lead?.email || 'email on file'}). The agreement will be marked as sent and ready for client review and signature.`,
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/agreements/${agreementId}/send`,
            {},
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchAgreements();
            onUpdate();
            showNotification('success', 'Email Sent!', response.data?.message || `Agreement emailed successfully to ${lead?.email}. The client will receive a professional email with the PDF attachment.`);
          }
        } catch (error) {
          console.error('Error sending agreement:', error);
          showNotification('error', 'Email Failed', error.response?.data?.detail || 'Failed to send agreement email. Please check the lead has a valid email address.');
        }
      }
    });
  };
  
  // Reject Agreement
  const rejectAgreement = async (agreementId) => {
    setPromptModal({
      show: true,
      title: 'Reject Agreement',
      message: 'Please provide a reason for rejecting this agreement. This will be recorded in the agreement history.',
      placeholder: 'Enter rejection reason...',
      onConfirm: async (reason) => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/agreements/${agreementId}/approve`,
            { approved: false, rejection_reason: reason },
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchAgreements();
            onUpdate();
            showNotification('warning', 'Agreement Rejected', 'Agreement has been rejected with the provided reason.');
          }
        } catch (error) {
          console.error('Error rejecting agreement:', error);
          showNotification('error', 'Rejection Failed', error.response?.data?.detail || error.message);
        }
      }
    });
  };

  // Proposal Action Functions
  const sendProposal = async (proposalId) => {
    setConfirmation({
      show: true,
      type: 'info',
      title: 'Send Proposal via Email?',
      message: `This will send the proposal as a PDF attachment to the client's email address (${lead?.email || 'email on file'}). The proposal will be marked as sent and you'll be able to approve or reject it.`,
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/proposals/${proposalId}/send`,
            {},
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchProposals();
            onUpdate();
            showNotification('success', 'Email Sent!', response.data?.message || `Proposal emailed successfully to ${lead?.email}. The client will receive a professional email with the PDF attachment.`);
          }
        } catch (error) {
          console.error('Error sending proposal:', error);
          showNotification('error', 'Email Failed', error.response?.data?.detail || 'Failed to send proposal email. Please check the lead has a valid email address.');
        }
      }
    });
  };

  const handleScreenshotUpload = async (file, targetType, targetId) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = targetType === 'proposal' 
        ? `${BACKEND_URL}/api/proposals/${targetId}/upload-screenshot`
        : `${BACKEND_URL}/api/agreements/${targetId}/upload-screenshot`;
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.screenshot_url;
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    }
    return null;
  };

  const handleApprovalWithScreenshot = async () => {
    if (!screenshot) {
      showError('Please upload a screenshot of the email sent to the client', 'Screenshot Required');
      return;
    }

    if (!currentApprovalTarget) {
      showError('Invalid approval target', 'Error');
      return;
    }

    setApprovingWithScreenshot(true);
    try {
      // Upload screenshot first
      const screenshotUrl = await handleScreenshotUpload(screenshot, currentApprovalTarget.type, currentApprovalTarget.id);
      if (!screenshotUrl) {
        showError('Failed to upload screenshot. Please try again.', 'Upload Failed');
        return;
      }

      // Approve with AI verification
      const endpoint = currentApprovalTarget.type === 'proposal'
        ? `${BACKEND_URL}/api/proposals/${currentApprovalTarget.id}/approve`
        : `${BACKEND_URL}/api/agreements/${currentApprovalTarget.id}/approve`;

      const response = await axios.post(
        endpoint,
        { 
          approved: true,
          approval_method: 'email_validation',
          screenshot_file: screenshotUrl
        },
        { headers: getAuthHeader() }
      );

      if (response.status === 200) {
        // Reset modal state
        setScreenshotModal({ show: false, title: '', message: '', onConfirm: null });
        setScreenshot(null);
        setCurrentApprovalTarget(null);
        
        // Refresh data
        if (currentApprovalTarget.type === 'proposal') {
          fetchProposals();
        } else {
          fetchAgreements();
        }
        onUpdate();
        
        await showSuccess(
          `${currentApprovalTarget.type === 'proposal' ? 'Proposal' : 'Agreement'} approved successfully via AI email verification!`,
          'Approval Complete'
        );
      }
    } catch (error) {
      console.error('Error approving with screenshot:', error);
      showError(
        error.response?.data?.detail || 'Failed to approve. The AI could not verify the email from the screenshot.',
        'Approval Failed'
      );
    } finally {
      setApprovingWithScreenshot(false);
    }
  };

  const approveProposal = async (proposalId) => {
    // For non-admin users, show screenshot upload modal
    if (admin?.role !== 'admin' && admin?.role !== 'super_admin') {
      setCurrentApprovalTarget({ type: 'proposal', id: proposalId });
      setScreenshot(null);
      setScreenshotModal({
        show: true,
        title: 'Upload Customer Acknowledgement',
        message: 'To approve this proposal, please upload a screenshot of the email received from the customer showing their acceptance or acknowledgement of the proposal. Our AI will verify the customer email automatically.',
        onConfirm: handleApprovalWithScreenshot
      });
      return;
    }
    
    // For super_admin/admin, show confirmation and approve directly
    setConfirmation({
      show: true,
      type: 'success',
      title: 'Approve Proposal?',
      message: 'This will mark the proposal as approved and enable agreement generation for this lead.',
      onConfirm: async () => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/proposals/${proposalId}/approve`,
            { 
              approved: true,
              approval_method: 'super_admin_override'
            },
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchProposals();
            onUpdate();
            await showSuccess('Proposal approved successfully! You can now generate an agreement.', 'Proposal Approved');
          }
        } catch (error) {
          console.error('Error approving proposal:', error);
          showError(error.response?.data?.detail || 'Failed to approve proposal', 'Approval Failed');
        }
      }
    });
  };

  const rejectProposal = async (proposalId) => {
    setPromptModal({
      show: true,
      title: 'Reject Proposal',
      message: 'Please provide a reason for rejecting this proposal. This will be recorded in the proposal history.',
      placeholder: 'Enter rejection reason...',
      onConfirm: async (reason) => {
        try {
          const response = await axios.post(
            `${BACKEND_URL}/api/proposals/${proposalId}/reject`,
            { rejection_reason: reason },
            { headers: getAuthHeader() }
          );

          if (response.status === 200) {
            fetchProposals();
            onUpdate();
            showNotification('warning', 'Proposal Rejected', 'Proposal has been rejected with the provided reason.');
          }
        } catch (error) {
          console.error('Error rejecting proposal:', error);
          showNotification('error', 'Rejection Failed', error.response?.data?.detail || 'Failed to reject proposal');
        }
      }
    });
  };

  const handleProposalSuccess = (proposal) => {
    fetchProposals();
    onUpdate(); // Refresh lead data
  };
  
  // Check if lead stage allows proposal creation
  const canCreateProposal = () => {
    const allowedStages = ['qualified', 'proposal_sent', 'consultation_scheduled', 
      'consultation_completed', 'agreement_sent', 'agreement_signed', 
      'payment_partial', 'payment_full', 'payment_confirmation'];
    
    if (!allowedStages.includes(lead?.stage)) {
      return false;
    }
    
    // Check if there's already a pending proposal
    const hasPendingProposal = proposals.some(p => p.status === 'draft' || p.status === 'sent');
    return !hasPendingProposal;
  };
  
  // Check if lead has all mandatory information for proposal
  const getMissingMandatoryFields = () => {
    const missing = [];
    if (!lead?.email) missing.push('Email ID');
    if (!lead?.phone) missing.push('Phone Number');
    if (!lead?.service_type) missing.push('Service Type');
    if (!lead?.destination_country) missing.push('Destination Country');
    return missing;
  };
  
  // Check if lead stage allows invoice generation
  const canGenerateInvoice = () => {
    // Can generate invoice after agreement is approved
    const approvedAgreement = agreements.find(a => a.status === 'approved');
    // Allow invoice generation if agreement is approved and lead is in agreement_signed or later stages
    const allowedStages = ['agreement_signed', 'payment_confirmation', 'welcome_call', 'document_collection', 'application_preparation'];
    return approvedAgreement && allowedStages.includes(lead?.stage);
  };
  
  const handleGenerateInvoice = () => {
    // Find the approved agreement
    const approvedAgreement = agreements.find(a => a.status === 'approved');
    if (!approvedAgreement) {
      showError('No approved agreement found', 'Agreement Not Found');
      return;
    }
    
    // Multiple invoices are allowed for partial invoicing
    setShowInvoiceModal(true);
  };

  const fetchUpdateHistory = async () => {
    try {
      console.log('Fetching update history for lead:', lead.id);
      console.log('Auth header:', getAuthHeader());
      
      const response = await axios.get(
        `${BACKEND_URL}/api/leads/${lead.id}/updates`,
        { headers: getAuthHeader() }
      );
      
      console.log('Update history response:', response.data);
      setUpdateHistory(response.data || {
        current_updates: [],
        past_updates: [],
        last_reassignment: null,
        current_count: 0,
        past_count: 0
      });
    } catch (error) {
      console.error('Error fetching update history:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const fetchProcessOwners = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/users/process-consultants`,
        { headers: getAuthHeader() }
      );
      // The endpoint already filters for process consultants
      setProcessOwners(response.data || []);
    } catch (error) {
      console.error('Error fetching process consultants:', error);
    }
  };

  // Helper function to get role display name
  const getRoleDisplayName = (roleKey) => {
    const roleMap = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'process_consultant': 'Process Consultant',
      'sales': 'Visa Consultant'
    };
    return roleMap[roleKey] || roleKey;
  };

  const fetchLeadStages = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/lead-stages`,
        { headers: getAuthHeader() }
      );
      setLeadStages(response.data || []);
    } catch (error) {
      console.error('Error fetching lead stages:', error);
    }
  };

  const fetchUserRolePermissions = async () => {
    try {
      // Get current user's role from localStorage
      const adminData = localStorage.getItem('admin_data');
      if (!adminData) return;
      
      const parsedData = JSON.parse(adminData);
      const currentUserRole = parsedData.role;
      
      // Fetch all roles to get permissions
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/roles`,
        { headers: getAuthHeader() }
      );
      
      // Find current user's role object
      const roleObj = (response.data || []).find(r => r.role_key === currentUserRole);
      
      if (roleObj) {
        // Super Admin gets all stages
        if (currentUserRole === 'super_admin') {
          const allStageKeys = leadStages.map(s => s.stage_key);
          setUserRolePermissions({
            accessible_stages: allStageKeys,
            viewable_stages: []
          });
        } else {
          setUserRolePermissions({
            accessible_stages: roleObj.accessible_stages || [],
            viewable_stages: roleObj.viewable_stages || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching user role permissions:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (file) setResume(file);
  };

  const handleOtherDocsUpload = (e) => {
    const files = Array.from(e.target.files);
    setOtherDocs(prev => [...prev, ...files]);
  };

  const removeOtherDoc = (index) => {
    setOtherDocs(prev => prev.filter((_, i) => i !== index));
  };

  // Fetch lead documents
  const fetchLeadDocuments = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/lead-documents/${lead.id}`,
        { headers: getAuthHeader() }
      );
      setAttachedDocuments(response.data || []);
    } catch (error) {
      console.error('Error fetching lead documents:', error);
    }
  };

  // Handle file selection for upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setCustomDocName(file.name); // Default to original filename
      setShowUploadDialog(true);
    }
  };

  // Upload document with custom name
  const handleDocumentUpload = async () => {
    if (!selectedFile || !customDocName.trim()) {
      showError('Please provide a name for the document', 'Name Required');
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('custom_name', customDocName.trim());

      await axios.post(
        `${BACKEND_URL}/api/admin/upload/lead-document/${lead.id}`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Refresh document list
      await fetchLeadDocuments();
      
      // Reset state
      setShowUploadDialog(false);
      setSelectedFile(null);
      setCustomDocName('');
      
      await showSuccess('Document uploaded successfully!', 'Upload Complete');
    } catch (error) {
      console.error('Error uploading document:', error);
      showError('Failed to upload document. Please try again.', 'Upload Failed');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Preview document
  const handleDocumentPreview = async (doc) => {
    try {
      setPreviewLoading(true);
      
      // Use the downloadFile helper with preview parameter
      const response = await downloadFile(
        `${BACKEND_URL}/api/admin/lead-document/download/${lead.id}/${doc.id}?preview=true`,
        {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      );
      
      if (response.ok) {
        // Create blob URL for preview
        const blob = response.blob;
        const url = window.URL.createObjectURL(blob);
        
        // Set preview document with blob URL and metadata
        setPreviewDocument({
          name: doc.custom_name || doc.filename,
          url: url,
          type: doc.file_type,
          blob: blob
        });
      } else {
        if (response.status === 404) {
          showError('Document file not found on server. The file may have been deleted or moved.', 'File Not Found');
        } else {
          showError('Failed to preview document. Please try again.', 'Preview Failed');
        }
      }
    } catch (error) {
      console.error('Error previewing document:', error);
      if (error.status === 404) {
        showError('Document file not found on server. The file may have been deleted or moved.', 'File Not Found');
      } else {
        showError('Failed to preview document. Please try again.', 'Preview Failed');
      }
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Close preview modal
  const closePreviewModal = () => {
    try {
      if (previewDocument && previewDocument.url) {
        window.URL.revokeObjectURL(previewDocument.url);
      }
    } catch (error) {
      console.error('Error cleaning up preview URL:', error);
    }
    setPreviewDocument(null);
  };

  // Download document
  const handleDocumentDownload = async (doc) => {
    try {
      // Use the native downloadFile helper to avoid rrweb-recorder issues
      const response = await downloadFile(
        `${BACKEND_URL}/api/admin/lead-document/download/${lead.id}/${doc.id}`,
        {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      );
      
      if (response.ok) {
        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.custom_name || doc.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        showError('Failed to download document. Please try again.', 'Download Failed');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      // Provide specific error message for 404
      if (error.status === 404) {
        showError('Document file not found on server. The file may have been deleted or moved.', 'File Not Found');
      } else {
        showError('Failed to download document. Please try again.', 'Download Failed');
      }
    }
  };

  // Delete document
  const handleDocumentDelete = async (doc) => {
    if (!window.confirm(`Are you sure you want to delete "${doc.custom_name || doc.filename}"?`)) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/lead-document/${lead.id}/${doc.id}`,
        { headers: getAuthHeader() }
      );

      // Refresh document list
      await fetchLeadDocuments();
      
      alert('Document deleted successfully!');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert(error.response?.data?.detail || 'Failed to delete document. Please try again.');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Generate AI Follow-up
  const handleGenerateAiFollowup = async () => {
    setAiFollowupLoading(true);
    setAiFollowupResult(null);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/${lead.id}/ai-followup`,
        {},
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        setAiFollowupResult(response.data.suggestion);
        setShowAiFollowup(true);
        
        // Refresh update history to show the audit trail entry
        await fetchUpdateHistory();
      }
    } catch (error) {
      console.error('Error generating AI follow-up:', error);
      alert(error.response?.data?.detail || 'Failed to generate AI follow-up suggestion. Please try again.');
    } finally {
      setAiFollowupLoading(false);
    }
  };

  // Send AI-generated email
  const handleSendAiEmail = async () => {
    if (!aiFollowupResult || aiFollowupResult.action !== 'email') {
      showError('Email details not available', 'Cannot Send');
      return;
    }

    if (!aiFollowupResult.subject || !aiFollowupResult.message) {
      showError('Email subject or message is missing', 'Incomplete Data');
      return;
    }

    const confirmed = await showConfirm(
      `Send email to ${lead.name} (${lead.email})?\n\nSubject: ${aiFollowupResult.subject}\n\nThis will send a professional branded email with the AI-generated content.`,
      'Confirm Send Email'
    );

    if (!confirmed) return;

    setSendingEmail(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/${lead.id}/send-ai-email`,
        {
          subject: aiFollowupResult.subject,
          message: aiFollowupResult.message,
          suggestion_id: aiFollowupResult.suggestion_id
        },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        await showSuccess('Email sent successfully! Activity has been logged.', 'Email Sent');
        setShowAiFollowup(false);
        setAiFollowupResult(null);
        
        // Refresh update history to show the audit trail entry
        await fetchUpdateHistory();
        
        // Refresh lead data to show updated last_contacted
        onUpdate();
      }
    } catch (error) {
      console.error('Error sending AI email:', error);
      showError(error.response?.data?.detail || 'Failed to send email. Please try again.', 'Send Failed');
    } finally {
      setSendingEmail(false);
    }
  };

  // Fetch active email templates
  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/communication-templates`,
        { headers: getAuthHeader() }
      );
      // Filter only active email templates
      const activeEmailTemplates = response.data.filter(
        t => t.is_active && t.channel === 'email'
      );
      setEmailTemplates(activeEmailTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      showError('Failed to load email templates', 'Error');
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Handle opening template email modal
  const handleOpenTemplateEmailModal = () => {
    // Check if lead has a valid email address
    const invalidEmails = ['', null, undefined, 'noemail@placeholder.com'];
    if (invalidEmails.includes(lead.email) || !lead.email || !lead.email.includes('@')) {
      showError(
        'This lead does not have a valid email address. Please update the lead with a valid email before sending template emails.',
        'Invalid Email Address'
      );
      return;
    }
    
    fetchEmailTemplates();
    setShowTemplateEmailModal(true);
    setSelectedTemplate(null);
  };

  // Send template-based email
  const handleSendTemplateEmail = async () => {
    console.log('handleSendTemplateEmail called', { selectedTemplate, lead });
    
    if (!selectedTemplate) {
      showError('Please select a template first', 'No Template Selected');
      return;
    }

    const confirmed = await showConfirm(
      `Send "${selectedTemplate.name}" email to ${lead.name} (${lead.email})?\n\nSubject: ${selectedTemplate.subject_template || 'No subject set'}`,
      'Confirm Send Template Email'
    );

    if (!confirmed) {
      console.log('User cancelled send');
      return;
    }

    console.log('Sending template email...');
    setSendingTemplateEmail(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/${lead.id}/send-template-email`,
        {
          template_id: selectedTemplate.id
        },
        { headers: getAuthHeader() }
      );

      console.log('Response:', response.data);

      if (response.data.success) {
        await showSuccess(`Template email "${selectedTemplate.name}" sent successfully to ${lead.email}!`, 'Email Sent');
        setShowTemplateEmailModal(false);
        setSelectedTemplate(null);
        
        // Refresh update history
        await fetchUpdateHistory();
        
        // Refresh lead data
        onUpdate();
      }
    } catch (error) {
      console.error('Error sending template email:', error);
      showError(
        error.response?.data?.detail || 'Failed to send template email. Please try again.',
        'Send Failed'
      );
    } finally {
      setSendingTemplateEmail(false);
    }
  };


  const handleSave = async (onHold = false) => {
    // Validate required fields
    console.log('handleSave called with data:', { 
      update_type: formData.update_type, 
      update_remark: formData.update_remark,
      next_update_date: formData.next_update_date,
      next_update_time: formData.next_update_time,
      onHold 
    });
    
    if (!formData.update_type || !formData.update_remark) {
      showError('Please fill in both Update Type and Update Remark before saving!', 'Required Fields Missing');
      console.error('Validation failed - missing fields');
      return;
    }
    
    // Validate rejection reason if "Reject Lead" is selected
    if (formData.update_type === 'Reject Lead' && !formData.rejection_reason) {
      showError('Please select a Reason for Rejection!', 'Required Field Missing');
      console.error('Validation failed - missing rejection reason');
      return;
    }
    
    // Validate country selection if "Open Work Visa" is selected as rejection reason
    if (formData.update_type === 'Reject Lead' && formData.rejection_reason === 'Open Work Visa') {
      if (!formData.rejection_country) {
        showError('Please select a Country for Open Work Visa rejection!', 'Required Field Missing');
        console.error('Validation failed - missing rejection country for Open Work Visa');
        return;
      }
    }
    
    // Validate next update date and time are mandatory (except for "Reject Lead")
    if (formData.update_type !== 'Reject Lead' && (!formData.next_update_date || !formData.next_update_time)) {
      showError('Next Follow-Up Date and Time are mandatory! Please schedule your next update.', 'Required Field Missing');
      console.error('Validation failed - missing next update date/time');
      return;
    }
    
    setLoading(true);
    try {
      // Get rejection country - CountryDropdown already handles Schengen/Custom selection internally
      const finalRejectionCountry = (formData.update_type === 'Reject Lead' && formData.rejection_reason === 'Open Work Visa')
        ? formData.rejection_country
        : null;

      const updateData = {
        ...formData,
        on_hold: onHold,
        rejection_country: finalRejectionCountry,
        next_update_datetime: formData.next_update_date && formData.next_update_time 
          ? `${formData.next_update_date}T${formData.next_update_time}`
          : null
      };

      console.log('Updating lead with data:', updateData);

      // Update lead
      await axios.put(
        `${BACKEND_URL}/api/leads/${lead.id}`,
        updateData,
        { headers: getAuthHeader() }
      );
      
      console.log('Lead updated successfully');

      // Create update log entry
      if (formData.update_type && formData.update_remark) {
        console.log('Creating update log entry with data:', {
          type: formData.update_type,
          remark: formData.update_remark,
          next_update_datetime: updateData.next_update_datetime
        });
        
        const updateResponse = await axios.post(
          `${BACKEND_URL}/api/leads/${lead.id}/updates`,
          {
            type: formData.update_type,
            remark: formData.update_remark,
            rejection_reason: formData.rejection_reason || null,
            rejection_country: finalRejectionCountry,
            next_update_datetime: updateData.next_update_datetime
          },
          { headers: getAuthHeader() }
        );
        
        console.log('✅ Update log created:', updateResponse.data);
        
        // Only mark notifications as completed if this update is addressing an overdue/missed notification
        // Don't auto-complete ALL notifications just because user made any update
        try {
          const notificationsResponse = await axios.get(
            `${BACKEND_URL}/api/scheduled-notifications/today`,
            { headers: getAuthHeader() }
          );
          
          // Find ONLY missed/overdue notifications for this lead (not future ones)
          const allNotifications = [
            ...(notificationsResponse.data.today || []),
            ...(notificationsResponse.data.missed || [])
          ];
          
          const now = new Date();
          const overdueNotifications = allNotifications.filter(
            n => n.lead_id === lead.id && 
                 (n.status === 'missed') && // Only missed notifications
                 new Date(n.scheduled_time) < now // And time has passed
          );
          
          // Mark overdue notifications as completed
          for (const notif of overdueNotifications) {
            await axios.put(
              `${BACKEND_URL}/api/scheduled-notifications/${notif.id}/complete`,
              {},
              { headers: getAuthHeader() }
            );
          }
          
          if (overdueNotifications.length > 0) {
            console.log(`✅ Marked ${overdueNotifications.length} overdue notification(s) as completed`);
          }
        } catch (notifError) {
          console.warn('Error completing notifications:', notifError);
          // Don't fail the update if notification completion fails
        }
        
        // Refresh update history
        await fetchUpdateHistory();
        console.log('✅ Update history refreshed');
      } else {
        console.warn('Skipping update log - missing update_type or remark');
      }

      // Upload files if any
      if (resume || otherDocs.length > 0) {
        const formDataFiles = new FormData();
        if (resume) formDataFiles.append('resume', resume);
        otherDocs.forEach((doc, index) => {
          formDataFiles.append(`document_${index}`, doc);
        });

        await axios.post(
          `${BACKEND_URL}/api/leads/${lead.id}/documents`,
          formDataFiles,
          { 
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      // Refresh lead data and update history
      onUpdate();
      await fetchUpdateHistory();
      
      // Don't close modal - show success message and keep modal open
      await showSuccess('Lead updated successfully! The update has been logged in the history.', 'Update Saved');
      
      // Reset form fields for next update
      setFormData(prev => ({
        ...prev,
        update_type: '',
        next_update_date: '',
        next_update_time: '',
        update_remark: ''
      }));
      
    } catch (error) {
      console.error('Error updating lead:', error);
      showError(error.response?.data?.detail || 'Failed to update lead. Please try again.', 'Update Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignData.assigned_to) {
      alert('Please select a user to reassign the lead to');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/leads/${lead.id}/assign`,
        {
          assigned_to: reassignData.assigned_to,
          reason: reassignData.reason || 'Manual reassignment by Super Admin'
        },
        { headers: getAuthHeader() }
      );

      alert(`Lead successfully reassigned to ${reassignData.assigned_to}`);
      setShowReassignModal(false);
      setReassignData({ assigned_to: '', reason: '' });
      await fetchUpdateHistory(); // Refresh to show new assignment history
      onUpdate(); // Refresh lead list
    } catch (error) {
      console.error('Error reassigning lead:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reassign lead. Please try again.';
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content update-lead-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '16px',
          marginBottom: '24px'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Update Lead: #{lead.id} - {lead.name}</span>
            <span style={{
              padding: '4px 12px',
              background: '#777db0',
              color: 'white',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '500',
              textTransform: 'capitalize'
            }}>
              {lead.stage?.replace(/_/g, ' ')}
            </span>
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Send Template Email Button - Priority Position */}
            <button 
              className="btn-primary" 
              style={{ 
                padding: '8px 16px', 
                fontSize: '14px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTemplateEmailModal();
              }}
            >
              <Mail size={16} />
              Send Email
            </button>
            {userRole === 'super_admin' && (
              <button 
                className="btn-primary" 
                style={{ padding: '8px 16px', fontSize: '14px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReassignModal(true);
                }}
              >
                Reassign Lead
              </button>
            )}
            <button 
              className="btn-secondary" 
              style={{ padding: '8px 16px', fontSize: '14px' }}
              onClick={(e) => {
                e.stopPropagation();
                // TODO: Open edit modal
                alert('Edit modal - To be implemented');
              }}
            >
              Edit
            </button>
            <button 
              className="btn-secondary" 
              style={{ padding: '8px 16px', fontSize: '14px' }}
              onClick={(e) => {
                e.stopPropagation();
                // TODO: Open view modal
                alert('View modal - To be implemented');
              }}
            >
              View
            </button>
            <button
              data-testid="push-digisense-btn"
              className="btn-primary"
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                background: lead.digisense_last_push?.success ? '#16a34a' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
              onClick={async (e) => {
                e.stopPropagation();
                if (!window.confirm('Push this lead to DigiSense?')) return;
                try {
                  const response = await axios.post(
                    `${BACKEND_URL}/api/leads/${lead.id}/push-digisense`,
                    {},
                    { headers: getAuthHeader() }
                  );
                  if (response.data.success) {
                    alert('Lead pushed to DigiSense successfully!');
                  } else {
                    alert(response.data.message || 'Unable to push lead. Please contact the DigiSense team.');
                  }
                  onUpdate();
                } catch (error) {
                  alert('Unable to push lead to DigiSense. Please try again or contact the DigiSense team.');
                }
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22L11 13L2 9L22 2Z"/></svg>
              {lead.digisense_last_push?.success ? 'Pushed' : 'Push to DigiSense'}
            </button>
            <button onClick={onClose} style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: '4px'
            }}>
              <X size={24} />
            </button>
          </div>
        </div>

        {/* DigiSense Push Status Bar */}
        {lead.digisense_last_push && (
          <div data-testid="digisense-status-bar" style={{
            padding: '8px 16px',
            marginBottom: '16px',
            borderRadius: '6px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: lead.digisense_last_push.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${lead.digisense_last_push.success ? '#bbf7d0' : '#fecaca'}`,
            color: lead.digisense_last_push.success ? '#166534' : '#991b1b'
          }}>
            <span>
              <strong>DigiSense:</strong> {lead.digisense_last_push.success ? 'Pushed successfully' : `Failed - ${lead.digisense_last_push.message}`}
            </span>
            <span style={{ color: '#6b7280' }}>
              {lead.digisense_last_push.pushed_at ? new Date(lead.digisense_last_push.pushed_at).toLocaleString() : ''}
            </span>
          </div>
        )}

        {/* Main Content - Two Column Layout with space for sticky footer */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', maxHeight: 'calc(90vh - 260px)', overflow: 'auto', paddingRight: '8px', paddingBottom: '16px' }}>
          
          {/* Left Column - Form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Update Type */}
            <div className="form-group">
              <label>Select Update Type *</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={formData.update_type}
                  onChange={(e) => handleInputChange('update_type', e.target.value)}
                  style={{ flex: 1 }}
                  required
                >
                  <option value="">Please Select</option>
                  {UPDATE_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                <Phone size={20} style={{ color: '#A08EA8' }} />
              </div>
              {!formData.update_type && (
                <small style={{ color: '#ef4444', fontSize: '12px' }}>Required field</small>
              )}
            </div>

            {/* Rejection Reason (shown only when "Reject Lead" is selected) */}
            {formData.update_type === 'Reject Lead' && (
              <div className="form-group">
                <label>Reason for Rejection *</label>
                <select
                  value={formData.rejection_reason}
                  onChange={(e) => handleInputChange('rejection_reason', e.target.value)}
                  style={{ 
                    borderColor: !formData.rejection_reason ? '#ef4444' : undefined 
                  }}
                  required
                >
                  <option value="">Please Select Reason</option>
                  {REJECTION_REASONS.map(reason => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
                {!formData.rejection_reason && (
                  <small style={{ color: '#ef4444', fontSize: '12px' }}>Required field</small>
                )}
              </div>
            )}

            {/* Country Selection for Open Work Visa Rejection */}
            {formData.update_type === 'Reject Lead' && formData.rejection_reason === 'Open Work Visa' && (
              <CountryDropdown
                value={formData.rejection_country}
                onChange={(country) => handleInputChange('rejection_country', country)}
                required={true}
                label="Select Country for Open Work Visa"
                placeholder="Please Select Country"
                error={!formData.rejection_country ? 'Required field for Open Work Visa' : null}
              />
            )}

            {/* Next Update Date & Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>Next Follow-Up Date {formData.update_type !== 'Reject Lead' && '*'}</label>
                <input
                  type="date"
                  value={formData.next_update_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => handleInputChange('next_update_date', e.target.value)}
                  style={{ 
                    cursor: 'pointer',
                    borderColor: (formData.update_type !== 'Reject Lead' && !formData.next_update_date) ? '#ef4444' : undefined
                  }}
                  required={formData.update_type !== 'Reject Lead'}
                />
                {formData.update_type !== 'Reject Lead' && !formData.next_update_date && (
                  <small style={{ color: '#ef4444', fontSize: '12px' }}>Required field</small>
                )}
                {formData.update_type === 'Reject Lead' && (
                  <small style={{ color: '#64748b', fontSize: '12px' }}>Optional for rejected leads</small>
                )}
              </div>
              <div className="form-group">
                <label>Next Follow-Up Time {formData.update_type !== 'Reject Lead' && '*'}</label>
                <input
                  type="time"
                  value={formData.next_update_time}
                  onChange={(e) => handleInputChange('next_update_time', e.target.value)}
                  style={{ 
                    cursor: 'pointer',
                    borderColor: (formData.update_type !== 'Reject Lead' && !formData.next_update_time) ? '#ef4444' : undefined
                  }}
                  required={formData.update_type !== 'Reject Lead'}
                />
                {formData.update_type !== 'Reject Lead' && !formData.next_update_time && (
                  <small style={{ color: '#ef4444', fontSize: '12px' }}>Required field</small>
                )}
                {formData.update_type === 'Reject Lead' && (
                  <small style={{ color: '#64748b', fontSize: '12px' }}>Optional for rejected leads</small>
                )}
              </div>
            </div>

            {/* Update Remark */}
            <div className="form-group">
              <label>Enter Update Remark *</label>
              <textarea
                value={formData.update_remark}
                onChange={(e) => handleInputChange('update_remark', e.target.value)}
                rows="6"
                placeholder="Enter detailed remarks about this update..."
                style={{ resize: 'vertical', minHeight: '120px' }}
                required
              />
              {!formData.update_remark && (
                <small style={{ color: '#ef4444', fontSize: '12px' }}>Required field</small>
              )}
            </div>

            {/* Personal Details Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label>DOB</label>
                <input
                  type="date"
                  value={formData.dob}
                  onChange={(e) => handleInputChange('dob', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Marital Status</label>
                <select
                  value={formData.marital_status}
                  onChange={(e) => handleInputChange('marital_status', e.target.value)}
                >
                  <option value="">Select</option>
                  {MARITAL_STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Sales Location</label>
                <select
                  value={formData.sales_location}
                  onChange={(e) => handleInputChange('sales_location', e.target.value)}
                >
                  <option value="">Select</option>
                  {SALES_LOCATIONS.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* File Uploads */}
            <div className="form-group">
              <label>Resume</label>
              <input
                type="file"
                onChange={handleResumeUpload}
                accept=".pdf,.doc,.docx"
              />
              {resume && <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{resume.name}</p>}
            </div>

            {/* Process Owner - Hidden in document collection, only super_admin can change */}
            {!isDocumentCollection && isSuperAdmin && (
              <div className="form-group">
                <label>Select Process Owner</label>
                <select
                  value={formData.process_owner}
                  onChange={(e) => handleInputChange('process_owner', e.target.value)}
                >
                  <option value="">Select</option>
                  {processOwners.map((owner, index) => (
                    <option key={owner.id || owner.email || owner.username || index} value={owner.username || owner.email}>
                      {owner.consultant_name || owner.username || owner.email} ({getRoleDisplayName(owner.role)})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Mark As - Hidden in document collection */}
            {!isDocumentCollection && (
              <div className="form-group">
                <label>Mark As</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {['hot', 'cold', 'warm'].map(temp => (
                    <button
                      key={temp}
                      type="button"
                      onClick={() => handleInputChange('temperature', temp)}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: formData.temperature === temp ? '2px solid #A08EA8' : '1px solid #d1d5db',
                        borderRadius: '6px',
                        background: formData.temperature === temp ? '#f3f4f6' : 'white',
                        cursor: 'pointer',
                        fontWeight: formData.temperature === temp ? '600' : '400',
                        textTransform: 'capitalize'
                      }}
                    >
                      {temp === 'hot' && '🔥'} {temp === 'cold' && '❄️'} {temp === 'warm' && '☀️'} {temp}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Document Attachments Section - Hidden in document collection (shown inside yellow section) */}
            {!isDocumentCollection && (
            <div className="form-group" style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px',
              background: '#f9fafb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ margin: 0, fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Paperclip size={18} style={{ color: '#A08EA8' }} />
                  Document Attachments
                </label>
                <label 
                  htmlFor="attach-doc" 
                  className="btn-primary" 
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    fontSize: '13px'
                  }}
                >
                  <Upload size={14} />
                  Attach File
                </label>
                <input
                  id="attach-doc"
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                />
              </div>

              {/* Document List */}
              {attachedDocuments.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', margin: '12px 0' }}>
                  No documents attached yet
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {attachedDocuments.map((doc) => (
                    <div 
                      key={doc.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px',
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                        <FileText size={18} style={{ color: '#A08EA8' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>
                            {doc.custom_name || doc.filename}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                            {formatFileSize(doc.file_size)} • Uploaded by {doc.uploaded_by} • {new Date(doc.uploaded_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleDocumentPreview(doc)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#8b5cf6',
                            padding: '4px'
                          }}
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleDocumentDownload(doc)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#3b82f6',
                            padding: '4px'
                          }}
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => handleDocumentDelete(doc)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#ef4444',
                            padding: '4px'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* AI Sales Assistant */}
            <AISalesAssistant 
              leadId={lead.id}
              serviceName={lead.service_type}
              country={lead.destination_country}
              onAnswerGenerated={(answer) => {
                console.log('AI Answer generated:', answer);
              }}
            />

            {/* AI Follow-Up or Document Follow-Up Button based on stage - Hidden in document_collection (shown inside yellow section) */}
            {!isDocumentCollection && (
            <div style={{ marginTop: '16px' }}>
              <button
                onClick={handleGenerateAiFollowup}
                disabled={aiFollowupLoading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: isDocumentCollection 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: aiFollowupLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <Sparkles size={18} />
                {aiFollowupLoading 
                  ? (isDocumentCollection ? 'Generating Document Follow-Up...' : 'Generating AI Suggestion...') 
                  : (isDocumentCollection ? '📄 Send Document Follow-Up Email' : '✨ Generate AI Follow-Up')}
              </button>
            </div>
            )}

            {/* AI Follow-Up Result - Hidden in document_collection (shown inside yellow section) */}
            {!isDocumentCollection && showAiFollowup && aiFollowupResult && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                border: '2px solid #667eea',
                borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                    🤖 AI Follow-Up Suggestion
                  </h4>
                  <button
                    onClick={() => setShowAiFollowup(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    <X size={18} />
                  </button>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                    Recommended Action:
                  </div>
                  <div style={{ 
                    padding: '8px 12px', 
                    background: '#667eea', 
                    color: 'white', 
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'inline-block'
                  }}>
                    {aiFollowupResult.action}
                  </div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                    Suggested Message:
                  </div>
                  <div style={{ 
                    padding: '12px', 
                    background: 'white', 
                    borderRadius: '6px',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    border: '1px solid #d1d5db'
                  }}>
                    {aiFollowupResult.message}
                  </div>
                </div>

                {/* Show email subject if action is email */}
                {aiFollowupResult.action === 'email' && aiFollowupResult.subject && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                      Email Subject:
                    </div>
                    <div style={{ 
                      padding: '10px 12px', 
                      background: 'white', 
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#374151',
                      border: '1px solid #d1d5db'
                    }}>
                      {aiFollowupResult.subject}
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                    AI Reasoning:
                  </div>
                  <div style={{ 
                    padding: '10px', 
                    background: '#fef3c7', 
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#78350f',
                    fontStyle: 'italic'
                  }}>
                    {aiFollowupResult.reasoning}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    Confidence: {Math.round((aiFollowupResult.confidence || 0.7) * 100)}%
                  </div>
                  
                  {/* Send Email Button - only show if action is email */}
                  {aiFollowupResult.action === 'email' && (
                    <button
                      onClick={handleSendAiEmail}
                      disabled={sendingEmail}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: sendingEmail ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Send size={14} />
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Proposal Management - Hidden in document collection stage */}
            {!isDocumentCollection && hasPermission(admin, 'view_proposals') && (lead?.stage === 'qualified' || lead?.stage === 'proposal_sent') && hasPermission(admin, 'create_proposals') && (
              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={() => {
                    const missing = getMissingMandatoryFields();
                    if (missing.length > 0) {
                      alert(`Cannot create proposal. Please update the lead with the following mandatory information: ${missing.join(', ')}`);
                      return;
                    }
                    setShowProposalModal(true);
                  }}
                  disabled={!canCreateProposal() || getMissingMandatoryFields().length > 0}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: (canCreateProposal() && getMissingMandatoryFields().length === 0)
                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (canCreateProposal() && getMissingMandatoryFields().length === 0) ? 'pointer' : 'not-allowed',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    opacity: (canCreateProposal() && getMissingMandatoryFields().length === 0) ? 1 : 0.6
                  }}
                >
                  <IndianRupee size={18} />
                  Generate & Share Proposal
                </button>
                {!canCreateProposal() && proposals.some(p => p.status === 'draft' || p.status === 'sent') && (
                  <p style={{ 
                    marginTop: '8px', 
                    fontSize: '12px', 
                    color: '#ef4444',
                    textAlign: 'center'
                  }}>
                    ⚠️ Please approve or reject the pending proposal before creating a new one
                  </p>
                )}
                
                {/* Warning for missing mandatory fields */}
                {getMissingMandatoryFields().length > 0 && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '12px',
                    background: '#fef3c7',
                    border: '1px solid #fbbf24',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#92400e'
                  }}>
                    <strong>⚠️ Missing Required Information:</strong>
                    <div style={{ marginTop: '4px' }}>
                      Please update the lead with: <strong>{getMissingMandatoryFields().join(', ')}</strong>
                    </div>
                  </div>
                )}
                
                {/* Show existing proposals */}
                {hasPermission(admin, 'view_proposals') && proposals.length > 0 && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#166534' }}>
                      Proposals ({proposals.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {proposals.map(proposal => (
                        <div key={proposal.id} style={{ 
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#374151' }}>{proposal.proposal_number}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>₹{Math.round(proposal.total_amount || 0).toLocaleString()}</div>
                            </div>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              background: proposal.status === 'approved' ? '#d1fae5' : 
                                         proposal.status === 'rejected' ? '#fee2e2' : 
                                         proposal.status === 'sent' ? '#dbeafe' : '#f3f4f6',
                              color: proposal.status === 'approved' ? '#065f46' : 
                                     proposal.status === 'rejected' ? '#991b1b' : 
                                     proposal.status === 'sent' ? '#1e40af' : '#374151'
                            }}>
                              {proposal.status}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {hasPermission(admin, 'download_proposals') && (
                              <button
                                onClick={async () => {
                                  try {
                                    await handleDownloadWithHistory(
                                      `${BACKEND_URL}/api/proposals/${proposal.id}/download-pdf`,
                                      `proposal_${proposal.proposal_number.replace(/\//g, '_')}.pdf`
                                    );
                                  } catch (error) {
                                    console.error('Error downloading PDF:', error);
                                    showError('Failed to download PDF', 'Download Error');
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                📄 Download
                              </button>
                            )}
                            
                            {proposal.status === 'draft' && (
                              <button
                                onClick={() => sendProposal(proposal.id)}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#8b5cf6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                📤 Send
                              </button>
                            )}
                            
                            {(proposal.status === 'sent' || proposal.status === 'approved') && (
                              <button
                                onClick={() => sendProposal(proposal.id)}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                🔄 Re-Send
                              </button>
                            )}
                            
                            {proposal.status === 'sent' && (
                              <>
                                <button
                                  onClick={() => approveProposal(proposal.id)}
                                  style={{
                                    flex: 1,
                                    minWidth: '80px',
                                    padding: '6px 12px',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  onClick={() => rejectProposal(proposal.id)}
                                  style={{
                                    flex: 1,
                                    minWidth: '80px',
                                    padding: '6px 12px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✗ Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Proposal Details & Agreement Generation - Proposal Approved Stage - Hidden in document collection */}
            {!isDocumentCollection && lead?.stage === 'proposal_approved' && hasPermission(admin, 'view_proposals') && (
              <div style={{ marginTop: '16px' }}>
                {/* Approved Proposal Details */}
                {proposals.some(p => p.status === 'approved') && (
                  <div style={{ 
                    padding: '16px', 
                    background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', 
                    border: '1px solid #10b981', 
                    borderRadius: '12px',
                    marginBottom: '16px' 
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '12px' 
                    }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '16px', 
                        fontWeight: '700', 
                        color: '#065f46',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        ✅ Approved Proposal
                      </h3>
                      <span style={{
                        padding: '6px 12px',
                        background: '#059669',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        READY FOR AGREEMENT
                      </span>
                    </div>
                    
                    {proposals.filter(p => p.status === 'approved').map(proposal => (
                      <div key={proposal.id} style={{ 
                        background: 'rgba(255, 255, 255, 0.8)',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.2)'
                      }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                          gap: '12px',
                          marginBottom: '12px' 
                        }}>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Proposal Number</div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                              {proposal.proposal_number}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Total Amount</div>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: '#059669' }}>
                              ₹{proposal.total_amount.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Service Type</div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                              {proposal.service_type}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Approved Date</div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                              {proposal.approved_at ? new Date(proposal.approved_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {hasPermission(admin, 'download_proposals') && (
                            <button
                              onClick={async () => {
                                try {
                                  await handleDownloadWithHistory(
                                    `${BACKEND_URL}/api/proposals/${proposal.id}/download-pdf`,
                                    `proposal_${proposal.proposal_number.replace(/\//g, '_')}.pdf`
                                  );
                                } catch (error) {
                                  console.error('Error downloading PDF:', error);
                                  showError('Failed to download PDF', 'Download Error');
                                }
                              }}
                              style={{
                                padding: '8px 16px',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}
                            >
                              <Download size={14} />
                              Download Proposal
                            </button>
                          )}
                          
                          <button
                            onClick={() => sendProposal(proposal.id)}
                            style={{
                              padding: '8px 16px',
                              background: '#8b5cf6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            🔄 Re-Send Proposal
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Generate Agreement Button - Prominent for Proposal Approved stage */}
                {canCreateAgreement() && hasPermission(admin, 'create_agreements') && (
                  <div style={{ marginBottom: '16px' }}>
                    <button
                      onClick={() => setShowAgreementModal(true)}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        fontSize: '16px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <FileText size={20} />
                      🎉 Generate Service Agreement
                    </button>
                    <div style={{ 
                      marginTop: '8px', 
                      fontSize: '12px', 
                      color: '#6b7280', 
                      textAlign: 'center' 
                    }}>
                      Ready to create your service agreement based on the approved proposal
                    </div>
                  </div>
                )}

                {/* Next Steps Guide */}
                <div style={{ 
                  padding: '12px', 
                  background: '#f0f9ff', 
                  border: '1px solid #0ea5e9', 
                  borderRadius: '8px',
                  fontSize: '13px' 
                }}>
                  <h4 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#0369a1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    📋 Next Steps
                  </h4>
                  <ol style={{ margin: '0', paddingLeft: '16px', color: '#374151' }}>
                    <li style={{ marginBottom: '4px' }}>Generate and send the service agreement</li>
                    <li style={{ marginBottom: '4px' }}>Wait for client agreement approval</li>
                    <li style={{ marginBottom: '4px' }}>Generate invoice once agreement is signed</li>
                    <li style={{ marginBottom: '4px' }}>Collect payment and move to process stage</li>
                  </ol>
                </div>
              </div>
            )}
            
            {/* Show existing agreements - Hidden in document collection stage */}
            {!isDocumentCollection && hasPermission(admin, 'view_agreements') && agreements.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#5b21b6' }}>
                      Agreements ({agreements.length})
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {agreements.map(agreement => (
                        <div key={agreement.id} style={{ 
                          padding: '8px',
                          background: 'white',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#374151' }}>{agreement.agreement_number}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>₹{Math.round(agreement.total_amount || 0).toLocaleString()}</div>
                            </div>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              background: agreement.status === 'approved' ? '#d1fae5' : 
                                         agreement.status === 'rejected' ? '#fee2e2' : 
                                         agreement.status === 'sent' ? '#dbeafe' : '#f3f4f6',
                              color: agreement.status === 'approved' ? '#065f46' : 
                                     agreement.status === 'rejected' ? '#991b1b' : 
                                     agreement.status === 'sent' ? '#1e40af' : '#374151'
                            }}>
                              {agreement.status}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {hasPermission(admin, 'download_agreements') && (
                              <button
                                onClick={() => downloadAgreementPDF(agreement.id, agreement.agreement_number)}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#777db0',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                📄 Download
                              </button>
                            )}
                            
                            {agreement.status === 'draft' && (
                              <button
                                onClick={() => sendAgreement(agreement.id)}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                📤 Send
                              </button>
                            )}
                            
                            {(agreement.status === 'sent' || agreement.status === 'approved') && (
                              <button
                                onClick={() => sendAgreement(agreement.id)}
                                style={{
                                  flex: 1,
                                  minWidth: '90px',
                                  padding: '6px 12px',
                                  background: '#6366f1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  fontWeight: '600'
                                }}
                              >
                                🔄 Re-Send
                              </button>
                            )}
                            
                            {agreement.status === 'sent' && (
                              <>
                                <button
                                  onClick={() => approveAgreement(agreement.id)}
                                  style={{
                                    flex: 1,
                                    minWidth: '80px',
                                    padding: '6px 12px',
                                    background: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✓ Approve
                                </button>
                                <button
                                  onClick={() => rejectAgreement(agreement.id)}
                                  style={{
                                    flex: 1,
                                    minWidth: '80px',
                                    padding: '6px 12px',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '600'
                                  }}
                                >
                                  ✗ Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
              </div>
            )}

            
            {/* Invoice Generation - Hidden in document collection stage */}
            {!isDocumentCollection && hasPermission(admin, 'view_invoices') && canGenerateInvoice() && hasPermission(admin, 'create_invoices') && (
              <div style={{ marginTop: '16px' }}>
                <button
                  onClick={handleGenerateInvoice}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px'
                  }}
                >
                  <Receipt size={18} />
                  Generate Invoice
                </button>
              </div>
            )}
            
            {/* Show existing invoices - Hidden in document collection stage */}
            {!isDocumentCollection && hasPermission(admin, 'view_invoices') && invoices.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ padding: '12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#92400e' }}>
                    Invoices ({invoices.length})
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {invoices.map(invoice => (
                      <div key={invoice.id} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px',
                        background: 'white',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#374151' }}>{invoice.invoice_number}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280' }}>
                            ₹{Math.round(invoice.amount_paid || 0).toLocaleString()} / ₹{Math.round(invoice.total_amount || 0).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: invoice.payment_status === 'paid' ? '#d1fae5' : 
                                       invoice.payment_status === 'partial' ? '#fef3c7' : '#fee2e2',
                            color: invoice.payment_status === 'paid' ? '#065f46' : 
                                   invoice.payment_status === 'partial' ? '#92400e' : '#991b1b'
                          }}>
                            {invoice.payment_status}
                          </span>
                          {hasPermission(admin, 'download_invoices') && (
                            <button
                              onClick={async () => {
                                try {
                                  await handleDownloadWithHistory(
                                    `${BACKEND_URL}/api/invoices/${invoice.id}/download-pdf`,
                                    `Invoice_${invoice.invoice_number.replace(/\//g, '_')}.pdf`
                                  );
                                } catch (error) {
                                  console.error('Download error:', error);
                                  showError('Failed to download invoice PDF', 'Download Error');
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                background: '#777db0',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Download size={12} />
                              PDF
                            </button>
                          )}
                          {hasPermission(admin, 'download_invoices') && (
                            <button
                              onClick={() => sendInvoice(invoice.id, invoice.invoice_number)}
                              style={{
                                padding: '4px 8px',
                                background: invoice.email_sent ? '#6366f1' : '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              {invoice.email_sent ? '🔄 Re-Send' : '📤 Send'}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                
                {/* Payment Tracking Section */}
                {hasPermission(admin, 'view_payments') && invoices.map(invoice => (
                  <PaymentTrackingSection
                    key={invoice.id}
                    invoice={invoice}
                    onPaymentRecorded={() => {
                      fetchInvoices();
                      onUpdate();
                    }}
                  />
                ))}
              </div>
            )}

            {/* Welcome Email Section - Show for welcome_call/document_collection stage */}
            {(lead?.stage === 'welcome_call' || lead?.stage === 'document_collection') && lead?.process_consultant && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ padding: '16px', background: '#ede9fe', border: '1px solid #a78bfa', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#5b21b6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    Welcome Onboarding
                  </h4>
                  <div style={{ fontSize: '13px', color: '#6b21a8', marginBottom: '12px' }}>
                    Process Consultant: <strong>{lead.process_consultant_name || lead.process_consultant}</strong>
                  </div>
                  {lead.welcome_email_sent_at && (
                    <div style={{ fontSize: '12px', color: '#7c3aed', marginBottom: '8px' }}>
                      ✓ Last sent: {new Date(lead.welcome_email_sent_at).toLocaleString()}
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm('Send welcome email to client with document checklist and ETA link?')) return;
                      try {
                        const response = await axios.post(
                          `${BACKEND_URL}/api/welcome/send-welcome-email/${lead.id}`,
                          {},
                          { headers: getAuthHeader() }
                        );
                        alert('✅ Welcome email sent successfully to ' + lead.email);
                        onUpdate(); // Refresh lead data
                      } catch (error) {
                        console.error('Failed to send welcome email:', error);
                        alert('❌ Failed to send welcome email: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    {lead.welcome_email_sent_at ? 'Resend Welcome Email' : 'Send Welcome Email'}
                  </button>
                  {lead.document_eta_token && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#e0e7ff', border: '1px solid #818cf8', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#3730a3', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                        </svg>
                        Customer ETA Link (share if email didn't reach)
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <input
                          type="text"
                          readOnly
                          value={`${window.location.origin}/public/welcome/eta/${lead.document_eta_token}`}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid #a5b4fc', borderRadius: '4px', fontSize: '12px', background: 'white', color: '#374151' }}
                          data-testid="eta-link-input"
                        />
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/public/welcome/eta/${lead.document_eta_token}`;
                            navigator.clipboard.writeText(link).then(() => {
                              alert('ETA link copied to clipboard!');
                            }).catch(() => {
                              const ta = document.createElement('textarea');
                              ta.value = link;
                              document.body.appendChild(ta);
                              ta.select();
                              document.execCommand('copy');
                              document.body.removeChild(ta);
                              alert('ETA link copied to clipboard!');
                            });
                          }}
                          style={{
                            padding: '6px 12px',
                            background: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                          data-testid="copy-eta-link-btn"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Document Collection Section - Only show in document_collection stage */}
            {isDocumentCollection && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ padding: '16px', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    📋 Document Collection Phase
                  </h4>
                  <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                    Process Consultant: <strong>{lead.process_consultant_name || lead.process_consultant}</strong>
                  </div>
                  
                  {lead.document_eta && (
                    <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '12px', padding: '8px', background: '#fef9c3', borderRadius: '4px' }}>
                      📅 Customer ETA: {new Date(lead.document_eta).toLocaleDateString()}
                    </div>
                  )}

                  <p style={{ fontSize: '13px', color: '#78350f', marginBottom: '12px' }}>
                    Client is in the document collection phase. Use the AI Follow-Up button below to send document reminder emails.
                  </p>

                  {/* Note about hidden sections */}
                  <div style={{ fontSize: '12px', color: '#a16207', fontStyle: 'italic', marginTop: '12px', padding: '8px', background: '#fef9c3', borderRadius: '4px' }}>
                    ℹ️ Proposal, Agreement, and Invoice management sections are hidden during document collection phase.
                  </div>
                </div>

                {/* Document Attachments Section - For document uploads */}
                <div style={{ marginTop: '16px', border: '1px solid #f59e0b', borderRadius: '8px', padding: '16px', background: '#fffbeb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <label style={{ margin: 0, fontSize: '15px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e' }}>
                      <Paperclip size={18} style={{ color: '#f59e0b' }} />
                      📎 Document Uploads
                    </label>
                    <label 
                      htmlFor="attach-doc" 
                      style={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: '13px',
                        background: '#f59e0b',
                        color: 'white',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: '600'
                      }}
                    >
                      <Upload size={14} />
                      Upload Document
                    </label>
                    <input
                      id="attach-doc"
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                    />
                  </div>

                  {/* Document List */}
                  {attachedDocuments.length === 0 ? (
                    <p style={{ color: '#92400e', fontSize: '13px', textAlign: 'center', margin: '12px 0' }}>
                      No documents uploaded yet. Upload required documents here.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {attachedDocuments.map((doc) => (
                        <div 
                          key={doc.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px',
                            background: 'white',
                            border: '1px solid #fbbf24',
                            borderRadius: '6px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                            <FileText size={18} style={{ color: '#f59e0b' }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>
                                {doc.custom_name || doc.filename}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                {formatFileSize(doc.file_size)} • Uploaded by {doc.uploaded_by} • {new Date(doc.uploaded_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleDocumentPreview(doc)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#8b5cf6',
                                padding: '4px'
                              }}
                              title="Preview"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              onClick={() => handleDocumentDownload(doc)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#3b82f6',
                                padding: '4px'
                              }}
                              title="Download"
                            >
                              <Download size={16} />
                            </button>
                            <button
                              onClick={() => handleDocumentDelete(doc)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#ef4444',
                                padding: '4px'
                              }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* AI Follow-Up Button - Document Collection specific */}
                <div style={{ marginTop: '16px' }}>
                  <button
                    onClick={handleGenerateAiFollowup}
                    disabled={aiFollowupLoading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: aiFollowupLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <Sparkles size={18} />
                    {aiFollowupLoading ? 'Generating Document Reminder...' : '📄 Send Document Follow-Up Email'}
                  </button>
                </div>

                {/* AI Follow-Up Result for Document Collection */}
                {showAiFollowup && aiFollowupResult && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#fffbeb',
                    border: '2px solid #f59e0b',
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#92400e' }}>
                        📄 Document Reminder Email
                      </h4>
                      <button
                        onClick={() => setShowAiFollowup(false)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        <X size={18} />
                      </button>
                    </div>
                    
                    {aiFollowupResult.subject && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#78350f', marginBottom: '4px', fontWeight: '600' }}>
                          Subject:
                        </div>
                        <div style={{ 
                          padding: '8px 12px', 
                          background: 'white', 
                          border: '1px solid #fbbf24',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#1f2937'
                        }}>
                          {aiFollowupResult.subject}
                        </div>
                      </div>
                    )}

                    {aiFollowupResult.message && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', color: '#78350f', marginBottom: '4px', fontWeight: '600' }}>
                          Email Content:
                        </div>
                        <div style={{ 
                          padding: '12px', 
                          background: 'white', 
                          border: '1px solid #fbbf24',
                          borderRadius: '6px',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: '#374151',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {aiFollowupResult.message}
                        </div>
                      </div>
                    )}

                    {aiFollowupResult.action === 'email' && (
                      <button
                        onClick={handleSendAiEmail}
                        disabled={sendingEmail}
                        style={{
                          padding: '10px 16px',
                          background: sendingEmail ? '#9ca3af' : '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: sendingEmail ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Send size={14} />
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Update History */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              padding: '16px',
              background: '#f9fafb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Contacted Update Log</h3>
                <button style={{ background: 'none', border: 'none', color: '#A08EA8', cursor: 'pointer', fontSize: '14px' }}>
                  Show
                </button>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', display: 'block' }}>Description</label>
                <input
                  type="text"
                  placeholder="Brief summary..."
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                />
              </div>

              {/* Update History Log */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Update History Log</h4>
                
                {/* Reassignment Info */}
                {updateHistory.last_reassignment && (
                  <div style={{ 
                    background: '#fff7ed', 
                    border: '1px solid #fed7aa', 
                    borderRadius: '6px', 
                    padding: '8px 12px', 
                    marginBottom: '12px',
                    fontSize: '12px'
                  }}>
                    <strong>Last Reassigned:</strong> to {updateHistory.last_reassignment.assigned_to} by {updateHistory.last_reassignment.assigned_by}<br/>
                    <strong>On:</strong> {new Date(updateHistory.last_reassignment.assigned_at).toLocaleString()}<br/>
                    <strong>Reason:</strong> {updateHistory.last_reassignment.reason}
                  </div>
                )}
                
                {/* Tabs */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => setActiveTab('current')}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'current' ? '600' : '400',
                      color: activeTab === 'current' ? '#A08EA8' : '#6b7280',
                      borderBottom: activeTab === 'current' ? '2px solid #A08EA8' : 'none',
                      paddingBottom: '8px',
                      fontSize: '14px'
                    }}
                  >
                    Current Updates ({updateHistory.current_count})
                  </button>
                  <button
                    onClick={() => setActiveTab('past')}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: activeTab === 'past' ? '600' : '400',
                      color: activeTab === 'past' ? '#A08EA8' : '#6b7280',
                      borderBottom: activeTab === 'past' ? '2px solid #A08EA8' : 'none',
                      paddingBottom: '8px',
                      fontSize: '14px'
                    }}
                  >
                    Past Updates ({updateHistory.past_count})
                  </button>
                </div>
                
                {/* Update List */}
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {(() => {
                    const updates = activeTab === 'current' ? updateHistory.current_updates : updateHistory.past_updates;
                    
                    if (!updates || updates.length === 0) {
                      return (
                        <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                          No {activeTab} updates yet
                        </p>
                      );
                    }
                    
                    // Sort updates by created_at in descending order (newest first)
                    const sortedUpdates = [...updates].sort((a, b) => {
                      const dateA = new Date(a.created_at);
                      const dateB = new Date(b.created_at);
                      return dateB - dateA; // Descending order
                    });
                    
                    return sortedUpdates.map((update, index) => (
                      <div
                        key={index}
                        style={{
                          borderLeft: '3px solid #A08EA8',
                          paddingLeft: '12px',
                          marginBottom: '16px',
                          paddingBottom: '12px',
                          borderBottom: index < updates.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}
                      >
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                          {update.type}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                          <strong>Updated At:</strong> {new Date(update.created_at).toLocaleString('en-IN', { 
                            timeZone: 'Asia/Kolkata',
                            dateStyle: 'medium',
                            timeStyle: 'medium'
                          })}
                        </div>
                        {update.next_update_datetime && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>Next Update:</strong> {new Date(update.next_update_datetime).toLocaleString('en-IN', {
                              timeZone: 'Asia/Kolkata',
                              dateStyle: 'medium',
                              timeStyle: 'medium'
                            })}
                          </div>
                        )}
                        {update.type === 'Reject Lead' && update.rejection_reason && (
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#dc2626', 
                            marginBottom: '6px',
                            padding: '6px 8px',
                            background: '#fee2e2',
                            borderRadius: '4px',
                            border: '1px solid #fecaca'
                          }}>
                            <strong>Rejection Reason:</strong> {update.rejection_reason}
                            {update.rejection_country && (
                              <span style={{ marginLeft: '8px' }}>
                                | <strong>Country:</strong> {update.rejection_country}
                              </span>
                            )}
                          </div>
                        )}
                        <div style={{ fontSize: '13px', color: '#374151', marginBottom: '6px' }}>
                          <strong>Remark:</strong> {update.remark}
                        </div>
                        
                        {/* AI Suggestion Details */}
                        {update.ai_suggestion_details && (
                          <div style={{ 
                            marginTop: '12px', 
                            padding: '12px', 
                            background: '#f0f9ff', 
                            borderRadius: '6px',
                            border: '1px solid #bfdbfe'
                          }}>
                            <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
                              🤖 AI Generated Content
                            </div>
                            
                            <div style={{ fontSize: '12px', marginBottom: '6px' }}>
                              <strong style={{ color: '#1f2937' }}>Action Type:</strong>
                              <span style={{ 
                                marginLeft: '6px',
                                padding: '2px 8px',
                                background: '#dbeafe',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#1e40af'
                              }}>
                                {update.ai_suggestion_details.action}
                              </span>
                            </div>
                            
                            {update.ai_suggestion_details.email_subject && (
                              <div style={{ fontSize: '12px', marginBottom: '6px', color: '#374151' }}>
                                <strong>Email Subject:</strong>
                                <div style={{ 
                                  marginTop: '4px',
                                  padding: '6px',
                                  background: '#fff',
                                  borderRadius: '4px',
                                  fontStyle: 'italic'
                                }}>
                                  {update.ai_suggestion_details.email_subject}
                                </div>
                              </div>
                            )}
                            
                            <div style={{ fontSize: '12px', marginBottom: '6px', color: '#374151' }}>
                              <strong>Suggested Message:</strong>
                              <div style={{ 
                                marginTop: '4px',
                                padding: '8px',
                                background: '#fff',
                                borderRadius: '4px',
                                whiteSpace: 'pre-wrap',
                                maxHeight: '200px',
                                overflowY: 'auto'
                              }}>
                                {update.ai_suggestion_details.message}
                              </div>
                            </div>
                            
                            {update.ai_suggestion_details.reasoning && (
                              <div style={{ fontSize: '12px', marginBottom: '6px', color: '#374151' }}>
                                <strong>AI Reasoning:</strong>
                                <div style={{ 
                                  marginTop: '4px',
                                  padding: '8px',
                                  background: '#fef3c7',
                                  borderRadius: '4px',
                                  fontStyle: 'italic',
                                  fontSize: '11px'
                                }}>
                                  {update.ai_suggestion_details.reasoning}
                                </div>
                              </div>
                            )}
                            
                            <div style={{ 
                              display: 'flex', 
                              gap: '12px', 
                              flexWrap: 'wrap',
                              fontSize: '11px', 
                              color: '#6b7280', 
                              marginTop: '8px',
                              paddingTop: '8px',
                              borderTop: '1px solid #bfdbfe'
                            }}>
                              <div>
                                <strong>Confidence Score:</strong> {update.ai_suggestion_details.confidence_score}%
                              </div>
                              {update.ai_suggestion_details.lead_score !== null && update.ai_suggestion_details.lead_score !== undefined && (
                                <div>
                                  <strong>Lead Score:</strong> {update.ai_suggestion_details.lead_score}
                                </div>
                              )}
                              {update.ai_suggestion_details.days_since_last_contact !== null && update.ai_suggestion_details.days_since_last_contact !== undefined && (
                                <div>
                                  <strong>Days Since Contact:</strong> {update.ai_suggestion_details.days_since_last_contact} days
                                </div>
                              )}
                              {update.ai_suggestion_details.lead_stage && (
                                <div>
                                  <strong>Lead Stage:</strong> {update.ai_suggestion_details.lead_stage}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          <strong>Updated by:</strong> {update.updated_by}
                        </div>
                        {update.stage && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            <strong>Updated At Stage:</strong> {update.stage}
                          </div>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Lead Summary at Bottom */}
            <div style={{ 
              background: '#f3f4f6', 
              padding: '12px', 
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '6px' }}>
                {lead.name} - #{lead.id}
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280' }}>
                <span><strong>Source:</strong> {lead.source}</span>
                <span><strong>Stage:</strong> {lead.stage}</span>
                {lead.service_type && <span><strong>Service:</strong> {lead.service_type}</span>}
                {lead.created_by && <span><strong>Created by:</strong> {lead.created_by}</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer with Action Buttons */}
        <div style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, #ffffff 0%, #ffffff 85%, rgba(255,255,255,0.9) 100%)',
          borderTop: '2px solid #e5e7eb',
          padding: '20px 24px',
          display: 'flex',
          gap: '12px',
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)',
          zIndex: 10,
          marginTop: '0'
        }}>
          <button
            className="btn-primary"
            onClick={() => handleSave(false)}
            disabled={loading || !formData.update_type || !formData.update_remark}
            style={{ 
              flex: 1,
              padding: '14px',
              fontSize: '15px',
              fontWeight: '600',
              opacity: (!formData.update_type || !formData.update_remark) ? 0.6 : 1,
              cursor: (loading || !formData.update_type || !formData.update_remark) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Updating...' : '✓ Update Lead'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={loading || !formData.update_type || !formData.update_remark}
            style={{
              flex: 1,
              padding: '14px',
              background: '#fbbf24',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !formData.update_type || !formData.update_remark) ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '15px',
              opacity: (!formData.update_type || !formData.update_remark) ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : '⏸ Save & On Hold'}
          </button>
        </div>
      </div>
      
      {/* Reassignment Modal */}
      {showReassignModal && (
        <div className="modal-overlay" onClick={() => setShowReassignModal(false)} style={{ zIndex: 10000 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Reassign Lead</h3>
              <button onClick={() => setShowReassignModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>Select User *</label>
              <select
                value={reassignData.assigned_to}
                onChange={(e) => setReassignData(prev => ({ ...prev, assigned_to: e.target.value }))}
                style={{ width: '100%' }}
                required
              >
                <option value="">Select user...</option>
                {processOwners.map(owner => (
                  <option key={owner.username} value={owner.username}>
                    {owner.username} ({getRoleDisplayName(owner.role)})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label>Reason for Reassignment</label>
              <textarea
                value={reassignData.reason}
                onChange={(e) => setReassignData(prev => ({ ...prev, reason: e.target.value }))}
                rows="3"
                placeholder="Enter reason for reassignment..."
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn-primary"
                onClick={handleReassign}
                disabled={loading || !reassignData.assigned_to}
                style={{ flex: 1 }}
              >
                {loading ? 'Reassigning...' : 'Reassign'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowReassignModal(false)}
                disabled={loading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Dialog */}
      {showUploadDialog && (
        <div className="modal-overlay" onClick={() => setShowUploadDialog(false)} style={{ zIndex: 10001 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Upload Document</h3>
              <button 
                onClick={() => {
                  setShowUploadDialog(false);
                  setSelectedFile(null);
                  setCustomDocName('');
                }} 
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>
            
            {selectedFile && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Selected File:</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>{selectedFile.name}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {formatFileSize(selectedFile.size)}
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label>Document Name *</label>
              <input
                type="text"
                value={customDocName}
                onChange={(e) => setCustomDocName(e.target.value)}
                placeholder="Enter a name for this document (e.g., Passport Copy, IELTS Certificate)"
                style={{ width: '100%' }}
                autoFocus
              />
              <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Give a meaningful name to identify this document easily
              </small>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn-primary"
                onClick={handleDocumentUpload}
                disabled={uploadingDoc || !customDocName.trim()}
                style={{ flex: 1 }}
              >
                {uploadingDoc ? 'Uploading...' : 'Upload'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowUploadDialog(false);
                  setSelectedFile(null);
                  setCustomDocName('');
                }}
                disabled={uploadingDoc}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Proposal Modal */}
      {showProposalModal && (
        <ProposalModal
          lead={lead}
          onClose={() => setShowProposalModal(false)}
          onSuccess={handleProposalSuccess}
        />
      )}

      
      {/* Agreement Modal */}
      {showAgreementModal && (
        <AgreementModal
          leadId={lead.id}
          onClose={() => setShowAgreementModal(false)}
          onSuccess={handleAgreementSuccess}
        />
      )}

      
      {/* Invoice Modal */}
      {showInvoiceModal && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          leadId={lead.id}
          agreementId={agreements.find(a => a.status === 'approved')?.id}
          onClose={() => setShowInvoiceModal(false)}
          onSuccess={() => {
            fetchInvoices();
            onUpdate();
            setShowInvoiceModal(false);
          }}
        />
      )}

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          animation: 'fadeIn 0.2s ease-in'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: notification.type === 'success' 
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : notification.type === 'error'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : notification.type === 'warning'
                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                : 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              color: 'white'
            }}>
              {notification.type === 'success' ? '✓' : 
               notification.type === 'error' ? '✕' :
               notification.type === 'warning' ? '⚠' : 'ℹ'}
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827',
              textAlign: 'center'
            }}>
              {notification.title}
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#6b7280',
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              {notification.message}
            </p>
            <button
              onClick={closeNotification}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #A08EA8 0%, #7D6882 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmation.show}
        onClose={() => setConfirmation({ ...confirmation, show: false })}
        onConfirm={confirmation.onConfirm}
        title={confirmation.title}
        message={confirmation.message}
        type={confirmation.type}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      {/* Prompt Modal */}
      <PromptModal
        show={promptModal.show}
        onClose={() => setPromptModal({ ...promptModal, show: false })}
        onConfirm={promptModal.onConfirm}
        title={promptModal.title}
        message={promptModal.message}
        placeholder={promptModal.placeholder}
        type="warning"
      />
      
      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alertState.isOpen}
        onClose={() => setAlertState(prev => ({ ...prev, isOpen: false }))}
        onConfirm={alertState.onConfirm}
        onCancel={alertState.onCancel}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* Screenshot Upload Modal for Non-Admin Approval */}
      {screenshotModal.show && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 10001 }}
          onClick={() => {
            setScreenshotModal({ show: false, title: '', message: '', onConfirm: null });
            setScreenshot(null);
            setCurrentApprovalTarget(null);
          }}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">{screenshotModal.title}</h2>
              <button
                onClick={() => {
                  setScreenshotModal({ show: false, title: '', message: '', onConfirm: null });
                  setScreenshot(null);
                  setCurrentApprovalTarget(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">{screenshotModal.message}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Screenshot <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setScreenshot(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Upload a clear screenshot of the email received from the customer as acceptance or acknowledgement. AI will verify the customer&apos;s email address.
                </p>
              </div>

              {screenshot && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <Check size={16} />
                    Screenshot selected: {screenshot.name}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={screenshotModal.onConfirm}
                  disabled={!screenshot || approvingWithScreenshot}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2 px-4 rounded-lg hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approvingWithScreenshot ? 'Verifying & Approving...' : `Approve ${currentApprovalTarget?.type === 'proposal' ? 'Proposal' : 'Agreement'}`}
                </button>
                <button
                  onClick={() => {
                    setScreenshotModal({ show: false, title: '', message: '', onConfirm: null });
                    setScreenshot(null);
                    setCurrentApprovalTarget(null);
                  }}
                  disabled={approvingWithScreenshot}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewDocument && (
        <div className="modal-overlay" onClick={closePreviewModal} style={{ zIndex: 10002, background: 'rgba(0, 0, 0, 0.85)' }}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              maxWidth: '90vw', 
              maxHeight: '90vh', 
              width: 'auto',
              padding: '24px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {previewDocument.name}
              </h3>
              <button 
                onClick={closePreviewModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#666',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {previewDocument.type?.toLowerCase() === '.pdf' ? (
                <iframe
                  src={previewDocument.url}
                  style={{ 
                    width: '100%', 
                    height: '70vh', 
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  title={previewDocument.name}
                />
              ) : ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(previewDocument.type?.toLowerCase()) ? (
                <img
                  src={previewDocument.url}
                  alt={previewDocument.name}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh', 
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ marginBottom: '20px', color: '#666' }}>
                    Preview not available for this file type ({previewDocument.type})
                  </p>
                  <button
                    className="btn-primary"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = previewDocument.url;
                      link.download = previewDocument.name;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = previewDocument.url;
                  link.download = previewDocument.name;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                Download
              </button>
              <button
                className="btn-secondary"
                onClick={closePreviewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Email Selection Modal */}
      {showTemplateEmailModal && (
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10002,
          animation: 'fadeIn 0.2s ease-in'
        }}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                  📧 Select Email Template
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                  Send to: {lead.name} ({lead.email})
                </p>
              </div>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTemplateEmailModal(false);
                  setSelectedTemplate(null);
                }}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Template List */}
            <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
              {loadingTemplates ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <div style={{ marginBottom: '12px' }}>Loading templates...</div>
                  <div style={{ width: '24px', height: '24px', border: '3px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
                </div>
              ) : emailTemplates.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Mail size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
                  <p style={{ color: '#6b7280', margin: 0 }}>No active email templates found</p>
                  <p style={{ color: '#9ca3af', fontSize: '13px', marginTop: '4px' }}>
                    Create email templates in the Communication Manager
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {emailTemplates.map(template => (
                    <div
                      key={template.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTemplate(template);
                      }}
                      style={{
                        padding: '16px',
                        border: `2px solid ${selectedTemplate?.id === template.id ? '#3b82f6' : '#e5e7eb'}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: selectedTemplate?.id === template.id ? '#eff6ff' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: '#111827', 
                            fontSize: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {selectedTemplate?.id === template.id && <Check size={16} style={{ color: '#3b82f6' }} />}
                            {template.name}
                          </div>
                          {template.description && (
                            <p style={{ 
                              margin: '4px 0 0', 
                              fontSize: '13px', 
                              color: '#6b7280',
                              lineHeight: '1.4'
                            }}>
                              {template.description}
                            </p>
                          )}
                          {template.subject_template && (
                            <div style={{ 
                              marginTop: '8px', 
                              padding: '8px 10px', 
                              background: '#f3f4f6', 
                              borderRadius: '6px',
                              fontSize: '12px',
                              color: '#374151'
                            }}>
                              <strong>Subject:</strong> {template.subject_template}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: '#dbeafe',
                            color: '#1e40af'
                          }}>
                            {template.trigger_event?.replace(/_/g, ' ')}
                          </span>
                          {template.attachments && template.attachments.length > 0 && (
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>
                              📎 {template.attachments.length} attachment{template.attachments.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTemplateEmailModal(false);
                  setSelectedTemplate(null);
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendTemplateEmail();
                }}
                disabled={!selectedTemplate || sendingTemplateEmail}
                style={{
                  padding: '10px 20px',
                  background: selectedTemplate ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: selectedTemplate && !sendingTemplateEmail ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Send size={16} />
                {sendingTemplateEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
